// Fan View (Titans webview demo), served at /fanview. Titans users only: App.js wraps the route in
// TitansRoute (logged in, valid session, venue titansNFL), and the "Fan View" sidebar link only shows
// for Titans users.
// The fan-facing queue times app: search, filter chips, locations grouped by area with live queue
// times, and a detail card per location. The three Titans locations are hardcoded below; their live
// queue times come from the same current-data routes the dashboard's Locations page uses.
// Ported from the standalone app: icons are Bootstrap Icons (the dashboard's set) instead of
// heroicons, and every class is prefixed "fv-" and scoped to .fv-app so nothing leaks into the
// dashboard's styles (see titansWebview.css).
import { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../../../contexts/auth-context';
import { getQueuesAPI } from '../../../../util';
import './titansWebview.css';

const REFRESH_SECONDS = 30;
const QUEUE_DISPLAY_CAP = 15;
const QUEUE_BUSY_THRESHOLD = 10;
const PLACEHOLDER_WORDS = ['Search']; // add words to enable rotation

// Bootstrap icon (<i class="bi bi-name">) sized by font-size; takes onClick like the heroicons did
const BI = ({ name, className = '', style, ...rest }) => (
  <i className={`${className} bi bi-${name}`} style={{ lineHeight: 1, ...style }} {...rest} />
);

// Filter chip icons (Bootstrap Icons). Categories by name; every area chip (a location's `location`)
// gets the area icon. Featured and any other chip have no icon.
const CATEGORY_ICONS = {
  Food: 'cup-straw',
  Restrooms: 'person-standing-dress',
  Gates: 'ticket-perforated-fill',
};
const AREA_ICON = 'geo-alt-fill';

// Hardcoded modal action buttons. onClick is a no-op for now.
const MODAL_ACTIONS = [
  { type: 'map',    label: 'Map',    icon: 'geo-alt-fill' },
  { type: 'wallet', label: 'Wallet', icon: 'wallet-fill' },
  { type: 'alerts', label: 'Alerts', icon: 'bell-fill' },
];

// Hardcoded location data. Used by fetchLocations until a real endpoint exists.
//
// Expected shape (one object per location):
// {
//   id: 'loc-001',              // string | number — unique together with `section`
//   name: 'North Restrooms',    // row + modal title, searchable (highest priority)
//   subtitle: 'Level 1',        // row + modal subtitle
//   location: 'Main Concourse', // groups rows into sections, becomes a filter chip, searchable
//   section: '104',             // string | number — sort tiebreaker, searchable
//   categories: ['Restrooms'],  // each becomes a filter chip
//   tags: ['bathroom', 'wc'],   // search-only keywords (lowest priority)
//   isFeatured: false,          // true = shown in the "Featured" section
//   queueTime: 4,               // minutes | null. null = no live data: hidden on home view, no chip
//   isOverflow: 0,              // 0 | 1. 1 = the queue runs past the sensors (the real queue is at least queueTime)
//   imageUrl: '',               // row + modal image (empty = blank placeholder)
//   description: '',            // modal body text (empty = hidden)
// }
// Titans' three deployed locations (ids, names, sections and images from the Titans location
// metadata). Area, subtitle, categories and tags are demo values: the metadata doesn't have them.
// queueTime / isOverflow start empty and are filled from the live routes on every refresh
// (fetchLocations); `live` says which route and which unit (section, gate, restroom) to read.
const HARDCODED_LOCATIONS = [
  {
    id: 2,
    name: 'Nashgrille To Go',
    subtitle: 'Section 117',
    location: 'Main Concourse',
    section: 117,
    categories: ['Food'],
    tags: ['food', 'concessions', 'grill', 'nashgrille', 'to', 'go', 'hamburgers', 'chicken tenders', 'grilled chicken sandwhich', 'hot chicken', 'fries'],
    isFeatured: false,
    queueTime: null,
    isOverflow: 0,
    live: { kind: 'concession', section: 117 },
    imageUrl: 'https://venue-queues-images.s3.us-east-2.amazonaws.com/NFL/TEN/concessions_nashgrille_to_go.png',
    description: 'Featuring Hamburgers, Chicken Tenders, Grilled Chicken Sandwich, Hot Chicken and Fries.',
  },
  {
    id: 3,
    name: 'Gate 9',
    subtitle: 'Section 120',
    location: 'Main Concourse',
    section: 120,
    categories: ['Gates'],
    tags: ['gate', 'entry', 'entrance', 'ticket', 'scan', 'gates'],
    isFeatured: false,
    queueTime: null,
    isOverflow: 0,
    live: { kind: 'gate', gateId: 1 },
    imageUrl: 'https://venue-queues-images.s3.us-east-2.amazonaws.com/NFL/TEN/gates_gate_9.png',
    description: 'Stadium entry gate. Gates open 2 hours before kickoff.',
  },
  {
    id: 4,
    name: 'Womens Restroom',
    subtitle: 'Section 101',
    location: 'Main Concourse',
    section: 101,
    categories: ['Restrooms'],
    tags: ['restroom', 'bathroom', 'women', 'womens', 'facilities'],
    isFeatured: false,
    queueTime: null,
    isOverflow: 0,
    live: { kind: 'restroom', restroomId: 1 },
    imageUrl: 'https://venue-queues-images.s3.us-east-2.amazonaws.com/NFL/TEN/facilities_womens_restroom.png',
    description: 'Womens restroom.',
  },
];

// ---------------------------------------------------------------------------------------------
// Live queue times: the same current-data routes (and request bodies) the dashboard's Locations
// page sends for these three locations, with the Titans metadata hardcoded.
// Each route returns 0 when the latest reading is more than a minute old; that's shown as no queue
// time (null). Overflow: only the concessions route returns it today; gates and restrooms read
// `overflow` too, so it starts working if those routes ever add it.
// ---------------------------------------------------------------------------------------------
const TITANS = { venue: 'titansNFL', timezone: 'America/Chicago' };

const LIVE_REQUESTS = {
  concession: {
    route: 'get-concession-location-current-data-dashboard-v2',
    body: { sections: [117], venue: TITANS.venue, locationId: 2, measurement: 'nashgrilleToGo' },
    // [{ section, queueTime, overflow, sensorActive, ... }]
    find: (data, live) => data.find((row) => String(row.section) === String(live.section)),
  },
  gate: {
    route: 'get-gates-location-current-data-dashboard',
    body: {
      gates: [{ gateId: 1, entranceSensorIds: [1, 2] }],
      venue: TITANS.venue,
      locationId: 3,
      measurement: 'gates',
      queueMeasurement: 'gatesQueues',
      timezone: TITANS.timezone,
    },
    // [{ gateId, queueTime, totalEntrances, totalExits }]
    find: (data, live) => data.find((row) => String(row.gateId) === String(live.gateId)),
  },
  restroom: {
    route: 'get-facilities-restrooms-location-current-data-dashboard-v2',
    body: {
      restrooms: [{ restroomId: 1, sectionId: 101 }],
      venue: TITANS.venue,
      locationId: 4,
      measurement: 'facilitiesRestrooms',
      queueMeasurement: 'facilitiesRestroomsQueues',
      timezone: TITANS.timezone,
    },
    // [{ restroomId, queueTime, sensorActive, entries, ... }]
    find: (data, live) => data.find((row) => String(row.restroomId) === String(live.restroomId)),
  },
};

const callRoute = (route, body, token) => new Promise((resolve, reject) => {
  getQueuesAPI(route, body, (data, error) => (error ? reject(error) : resolve(data)), token);
});

// Every location with its live queueTime / isOverflow. A route that fails leaves its location with
// no queue time; a 401 is passed on (signed out). Fails only if every route fails.
async function fetchLocations(token) {
  const kinds = Object.keys(LIVE_REQUESTS);
  const results = await Promise.allSettled(kinds.map((kind) => callRoute(LIVE_REQUESTS[kind].route, LIVE_REQUESTS[kind].body, token)));
  const unauthorized = results.find((r) => r.status === 'rejected' && r.reason?.status === 401);
  if (unauthorized) throw unauthorized.reason;
  if (results.every((r) => r.status === 'rejected')) throw new Error('Unable to load queue times');
  const dataByKind = Object.fromEntries(kinds.map((kind, i) => [kind, results[i].status === 'fulfilled' && Array.isArray(results[i].value) ? results[i].value : null]));

  return HARDCODED_LOCATIONS.map((loc) => {
    const request = LIVE_REQUESTS[loc.live?.kind];
    const data = request ? dataByKind[loc.live.kind] : null;
    const row = data ? request.find(data, loc.live) : null;
    const minutes = Number(row?.queueTime);
    const fresh = minutes > 0; // 0 = stale or no reading (a real queue is never below 1)
    return {
      ...loc,
      queueTime: fresh ? Math.round(minutes) : null,
      isOverflow: fresh && Number(row?.overflow) === 1 ? 1 : 0,
    };
  });
}

const hasQueue = (loc) => loc.queueTime != null;

// Overflow: the queue runs past the sensors, so the real queue is at least queueTime
const isOverflowing = (loc) => Number(loc.isOverflow) === 1;

// "5", "5+" (overflow), "15+" (over the display cap)
const getQueueDisplayTime = (loc) => {
  if (loc.queueTime > QUEUE_DISPLAY_CAP) return `${QUEUE_DISPLAY_CAP}+`;
  return isOverflowing(loc) ? `${loc.queueTime}+` : loc.queueTime;
};

// Red at the busy threshold or more, or whenever the queue is overflowing; green otherwise
const isBusy = (loc) => isOverflowing(loc) || loc.queueTime >= QUEUE_BUSY_THRESHOLD;
const getQueueColor = (loc) => (isBusy(loc) ? 'var(--fv-queue-busy)' : 'var(--fv-queue-ok)');

// "1 min" only for exactly 1 with no overflow ("1+ mins")
const isSingular = (loc) => loc.queueTime === 1 && !isOverflowing(loc);

// Fresh first, stale last (by section). Fresh: green before red, then shortest queue first within
// each colour. An overflowing queue ranks by its number ("5+" as 5, "12+" as 12) and, on the same
// number, after the plain one ("12" before "12+"); then section.
// Freshness split must come before numeric compare or null coerces to 0.
const bySection = (a, b) => String(a.section).localeCompare(String(b.section), undefined, { numeric: true });
const sortItems = (a, b) => {
  const aFresh = hasQueue(a);
  const bFresh = hasQueue(b);
  if (aFresh !== bFresh) return aFresh ? -1 : 1;
  if (!aFresh) return bySection(a, b);
  return (isBusy(a) - isBusy(b))
    || (a.queueTime - b.queueTime)
    || (isOverflowing(a) - isOverflowing(b))
    || bySection(a, b);
};

export default function TitansWebviewDemo() {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const [filterExpanded, setFilterExpanded] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [allLocations, setAllLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [selectedLocationKey, setSelectedLocationKey] = useState(null);
  const refreshTimeout = useRef(null);

  // Derived from allLocations so the modal stays in sync with refreshes
  const selectedLocation = selectedLocationKey
    ? allLocations.find(loc => loc.id === selectedLocationKey.id && loc.section === selectedLocationKey.section) || null
    : null;

  const loadLocations = useCallback(async (showSpinner = true) => {
    try {
      if (showSpinner) setIsLoading(true);
      setHasError(false);
      const locations = await fetchLocations(authCtx.token);
      setAllLocations(Array.isArray(locations) ? locations : []);
    } catch (err) {
      // Signed out or token expired: same as the dashboard's other pages
      if (err?.status === 401) {
        authCtx.logout();
        navigate('/auth');
        return;
      }
      console.error('Error fetching locations:', err);
      setHasError(true);
    } finally {
      if (showSpinner) setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authCtx.token]);

  // Fetch on mount + epoch-aligned refreshes
  useEffect(() => {
    loadLocations();

    const msUntilNextSync = () =>
      (REFRESH_SECONDS - (Math.floor(Date.now() / 1000) % REFRESH_SECONDS)) * 1000;

    const scheduleNextRefresh = () => {
      refreshTimeout.current = setTimeout(async () => {
        await loadLocations(false);
        scheduleNextRefresh();
      }, msUntilNextSync());
    };

    scheduleNextRefresh();
    return () => clearTimeout(refreshTimeout.current);
  }, [loadLocations]);

  // Refresh when returning to the page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) loadLocations(false);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadLocations]);

  // Animated placeholder rotation
  useEffect(() => {
    if (PLACEHOLDER_WORDS.length < 2 || isSearchFocused || searchQuery !== '') return;
    const interval = setInterval(() => {
      setHasAnimated(true);
      setPlaceholderIndex(prev => (prev + 1) % PLACEHOLDER_WORDS.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [isSearchFocused, searchQuery]);

  // Search with prioritized results: name → location/section → tags
  const searchLocations = (q) => {
    const query = q.toLowerCase().trim();
    if (!query) return allLocations;

    const nameMatches = [];
    const locationMatches = [];
    const tagMatches = [];

    allLocations.forEach(loc => {
      if (loc.name.toLowerCase().includes(query)) {
        nameMatches.push(loc);
      } else if (loc.location.toLowerCase().includes(query) || String(loc.section).includes(query)) {
        locationMatches.push(loc);
      } else if ((loc.tags || []).some(tag => tag.toLowerCase().includes(query))) {
        tagMatches.push(loc);
      }
    });

    return [...nameMatches, ...locationMatches, ...tagMatches];
  };

  const allCategories = [...new Set(allLocations.flatMap(loc => loc.categories || []))];
  const uniqueLocations = [...new Set(allLocations.map(loc => loc.location))];

  const filterOptions = [
    ...allCategories,
    ...(allLocations.some(loc => loc.isFeatured) ? ['Featured'] : []),
    ...uniqueLocations,
  ];

  const filterIcon = (filter) => CATEGORY_ICONS[filter] || (uniqueLocations.includes(filter) ? AREA_ICON : null);
  const chipLabel = (filter) => {
    const icon = filterIcon(filter);
    return <>{icon && <BI name={icon} className="fv-chip-icon" />}{filter}</>;
  };

  const toggleFilter = (filter) => {
    setSelectedFilters(prev =>
      prev.includes(filter) ? prev.filter(f => f !== filter) : [...prev, filter]
    );
  };

  const isHomeView = selectedFilters.length === 0 && searchQuery.trim() === '';

  const getSectionsToDisplay = () => {
    const noFiltersSelected = selectedFilters.length === 0;
    const categoryFilters = selectedFilters.filter(f => allCategories.includes(f));
    const locationFilters = selectedFilters.filter(f => uniqueLocations.includes(f));
    const featuredFilterSelected = selectedFilters.includes('Featured');

    const filteredLocations = searchLocations(searchQuery).filter(loc => {
      if (noFiltersSelected) return true;
      const matchesCategory = categoryFilters.length === 0 ||
        (loc.categories || []).some(cat => categoryFilters.includes(cat));
      const matchesLocation = locationFilters.length === 0 || locationFilters.includes(loc.location);
      const matchesFeatured = !featuredFilterSelected || loc.isFeatured;
      return matchesCategory && matchesLocation && matchesFeatured;
    });

    // Home view: only live locations. Search/filter: all matching, fresh-first.
    const visibleLocations = isHomeView ? filteredLocations.filter(hasQueue) : filteredLocations;
    const showFeatured = noFiltersSelected || featuredFilterSelected;
    const sections = [];

    if (showFeatured) {
      const featuredItems = visibleLocations.filter(loc => loc.isFeatured);
      if (featuredItems.length > 0) {
        sections.push({ title: 'Featured', items: featuredItems.sort(sortItems) });
      }
    }

    [...new Set(visibleLocations.map(loc => loc.location))].forEach(locationName => {
      const items = visibleLocations.filter(loc =>
        loc.location === locationName && !(showFeatured && loc.isFeatured)
      );
      if (items.length > 0) {
        sections.push({ title: locationName, items: items.sort(sortItems) });
      }
    });

    return sections;
  };

  const sectionsToDisplay = getSectionsToDisplay();

  // Pre-game: home view with nothing live yet
  const isPreGame = isHomeView && sectionsToDisplay.length === 0;
  const showPreGameEmpty = isPreGame && !isSearchFocused && !filterExpanded;

  // Pre-game only: focusing search auto-expands filters
  const handleSearchFocus = () => {
    setIsSearchFocused(true);
    if (isPreGame) setFilterExpanded(true);
  };

  const iconStyle = { fontSize: '1.25rem', color: '#68707c' };

  if (isLoading) {
    return (
      <div className="fv-app">
        <div className="fv-main-content">
          <div className="fv-loading-state">
            <div className="fv-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fv-app">
      {!hasError && allLocations.length > 0 && (
        <div className="fv-header">
          <div className="fv-search-bar">
            <BI name="search" className="fv-search-icon" style={iconStyle} />
            <div className="fv-search-input-wrapper">
              <input
                type="text"
                className="fv-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={handleSearchFocus}
                onBlur={() => setIsSearchFocused(false)}
              />
              {!isSearchFocused && !searchQuery && (
                <div className="fv-animated-placeholder">
                  <span
                    key={placeholderIndex}
                    className={`fv-placeholder-word ${PLACEHOLDER_WORDS.length > 1 ? (hasAnimated ? 'entering' : 'static') : ''}`}
                  >
                    {PLACEHOLDER_WORDS[placeholderIndex]}
                  </span>
                </div>
              )}
            </div>
            {searchQuery && (
              <BI name="x-lg" className="fv-filter-icon" role="button" aria-label="Clear search" onClick={() => setSearchQuery('')} style={iconStyle} />
            )}
            {filterExpanded ? (
              <BI name="chevron-up" className="fv-filter-icon" role="button" aria-label="Hide filters" onClick={() => setFilterExpanded(false)} style={iconStyle} />
            ) : (
              <BI name="funnel-fill" className="fv-filter-icon" role="button" aria-label="Show filters" onClick={() => setFilterExpanded(true)} style={iconStyle} />
            )}
          </div>

          {/* Active filters - condensed horizontal scroll (when collapsed) */}
          {!filterExpanded && selectedFilters.length > 0 && (
            <div className="fv-active-filters">
              {selectedFilters.map(filter => (
                <button key={filter} className="fv-filter-chip-active" onClick={() => toggleFilter(filter)}>
                  {chipLabel(filter)}
                </button>
              ))}
            </div>
          )}

          {/* Filter options - full grid */}
          {filterExpanded && (
            <div className="fv-filter-options expanded">
              {filterOptions.map(filter => (
                <button
                  key={filter}
                  className={`fv-filter-chip ${selectedFilters.includes(filter) ? 'selected' : ''}`}
                  onClick={() => toggleFilter(filter)}
                >
                  {chipLabel(filter)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="fv-main-content">
        {hasError ? (
          <div className="fv-empty-state">
            <BI name="exclamation-triangle" className="fv-empty-state-icon" style={{ fontSize: '3rem', color: '#000' }} />
            <div className="fv-empty-state-message">
              Unable to load queue times.{'\n'}
              Please check your connection and try again.
            </div>
          </div>
        ) : allLocations.length === 0 ? (
          <div className="fv-empty-state">
            <div className="fv-empty-state-message">
              Queue times are not available yet.{'\n'}
              Please check again later.
            </div>
          </div>
        ) : (
          <div className="fv-sections-wrapper">
            {sectionsToDisplay.map(section => (
              <div key={section.title} className="fv-section">
                <div className="fv-section-header">{section.title}</div>
                {section.items.map(item => (
                  <div
                    key={`${item.id}-${item.section}`}
                    className="fv-row"
                    onClick={() => setSelectedLocationKey({ id: item.id, section: item.section })}
                  >
                    <div className="fv-row-image" style={{ backgroundImage: `url(${item.imageUrl})` }}></div>
                    <div className="fv-row-content">
                      <div className="fv-row-title">{item.name}</div>
                      <div className="fv-row-subtitle">{item.subtitle}</div>
                    </div>
                    {hasQueue(item) && (
                      <div className="fv-queue-time" style={{ background: getQueueColor(item) }}>
                        <div className="fv-queue-time-number">{getQueueDisplayTime(item)}</div>
                        <div className="fv-queue-time-label">{isSingular(item) ? 'min' : 'mins'}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}

            {showPreGameEmpty && (
              <div className="fv-pregame-state">
                <div className="fv-empty-state-message">
                  Search to explore locations.{'\n'}
                  Queues will appear when locations open.
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Location detail modal */}
      {selectedLocation && (
        <div className="fv-modal-overlay" onClick={() => setSelectedLocationKey(null)}>
          <div className="fv-modal-card" onClick={(e) => e.stopPropagation()}>
            <button className="fv-modal-close" aria-label="Close" onClick={() => setSelectedLocationKey(null)}>
              <BI name="x-lg" style={{ fontSize: '0.75rem' }} />
            </button>

            <div className="fv-modal-header">
              <div className="fv-modal-image" style={{ backgroundImage: `url(${selectedLocation.imageUrl})` }} />
              <div className="fv-modal-title-group">
                <div className="fv-modal-title">{selectedLocation.name}</div>
                <div className="fv-modal-subtitle">{selectedLocation.subtitle}</div>
              </div>
            </div>

            <div className="fv-modal-body">
              {hasQueue(selectedLocation) && (
                <div className="fv-modal-wait-row">
                  <span className="fv-modal-wait-text" style={{ color: getQueueColor(selectedLocation) }}>
                    {getQueueDisplayTime(selectedLocation)} {isSingular(selectedLocation) ? 'minute' : 'minutes'}
                  </span>
                </div>
              )}

              <div className="fv-modal-actions">
                {MODAL_ACTIONS.map(({ type, label, icon }) => (
                  <button
                    key={type}
                    className="fv-modal-action-btn"
                    onClick={() => {
                      // TODO: wire up `type` action for selectedLocation
                    }}
                  >
                    <BI name={icon} style={{ fontSize: '1.1rem' }} />
                    {label}
                  </button>
                ))}
              </div>

              {selectedLocation.description && (
                <div className="fv-modal-description">{selectedLocation.description}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
